import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Plus, Save, Settings, Eye, Trash2, Moon, Sun, RefreshCw } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import WidgetModal from './WidgetModal';
import WidgetRenderer from './WidgetRenderer';
import API from '../Api';
import { useLogout } from '../hooks/useLogout';
import { useDashboardTimeRange } from '../hooks/useDashboardTimeRange';
import DashboardTimeRangeControls from './DashboardTimeRangeControls';
import './DashboardBuilder.css';
import UserTimezoneSelect from './UserTimezoneSelect';
import AskCommrayAi from './AskCommrayAi';
import { exportDashboardGridPng, exportWidgetImagesFromRoot } from '../utils/dashboardCapture';
import { DashboardChartSyncProvider } from '../context/DashboardChartSyncContext';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DashboardBuilder = () => {
  const { dashboardName: urlDashboardName } = useParams();
  const location = useLocation();
  const [layouts, setLayouts] = useState({
    lg: [],
    md: [],
    sm: [],
    xs: []
  });
  const [widgets, setWidgets] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboardDescription, setDashboardDescription] = useState('');
  const [savedDashboards, setSavedDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isEditMode, setIsEditMode] = useState(true);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [userInfo, setUserInfo] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedDashboardsOpen, setSavedDashboardsOpen] = useState(false);
  const [timeRangeOpen, setTimeRangeOpen] = useState(true);
  const {
    selection: timeSelection,
    updateSelection: updateTimeSelection,
    startTime,
    endTime,
    refreshTrigger,
    clear: clearDashboardTime,
    refreshNow
  } = useDashboardTimeRange(location.state);
  const [captureBusy, setCaptureBusy] = useState(false);
  const dashboardGridRef = useRef(null);
  const navigate = useNavigate();
  const handleLogout = useLogout();

  const handlePrintPage = useCallback(() => {
    window.print();
  }, []);

  const handleExportDashboardPng = useCallback(async () => {
    const name = dashboardName || 'dashboard';
    if (!widgets.length) return;
    try {
      setCaptureBusy(true);
      await exportDashboardGridPng(dashboardGridRef.current, name);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Could not export dashboard image.');
    } finally {
      setCaptureBusy(false);
    }
  }, [dashboardName, widgets.length]);

  const handleExportWidgetPngs = useCallback(async () => {
    const name = dashboardName || 'dashboard';
    if (!widgets.length) return;
    try {
      setCaptureBusy(true);
      await exportWidgetImagesFromRoot(dashboardGridRef.current, name);
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Could not export widget images.');
    } finally {
      setCaptureBusy(false);
    }
  }, [dashboardName, widgets.length]);

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        setLoading(true);
        setError(null);
        await fetchDashboards();
        await fetchUserInfo();
        
        if (urlDashboardName) {
          // Load specific dashboard from URL
          await loadDashboardByName(urlDashboardName);
        } else {
          // Check if this is a new dashboard creation (no URL parameter)
          // Don't load any existing dashboard, start fresh
          setDashboardName('');
          setDashboardDescription('');
          setWidgets([]);
          setLayouts({ lg: [], md: [], sm: [], xs: [] });
          setSelectedDashboard(null);
        }
      } catch (error) {
        console.error('Failed to initialize dashboard:', error);
        setError('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    initializeDashboard();
  }, [urlDashboardName]);

  const fetchUserInfo = async () => {
    try {
      const res = await API.get("/auth/user-info");
      if (res.data.username) {
        setUserInfo({
          username: res.data.username,
          role: res.data.role || "USER"
        });
        const userTheme = res.data.themePreference === "dark";
        setDarkMode(userTheme);
      } else {
        setUserInfo({
          username: localStorage.getItem("username") || "User",
          role: localStorage.getItem("userRole") || "USER"
        });
      }
    } catch (error) {
      console.error("Failed to fetch user info:", error);
      setUserInfo({
        username: localStorage.getItem("username") || "User",
        role: localStorage.getItem("userRole") || "USER"
      });
    }
  };

  const fetchDashboards = async () => {
    try {
      const response = await API.get('/api/dashboard');
      setSavedDashboards(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboards:', error);
      return [];
    }
  };

  const loadDashboardByName = async (name) => {
    try {
      // First, get all dashboards to find the one with matching name
      const dashboardsRes = await API.get('/api/dashboard');
      const dashboards = dashboardsRes.data || [];
      const dashboard = dashboards.find(d => d.name === name);
      
      if (dashboard) {
        // Use the dashboard ID to fetch the full dashboard data
        const response = await API.get(`/api/dashboard/${dashboard.id}`);
        const config = JSON.parse(response.data.dashboardConfig);
        
        setDashboardName(config.name);
        setDashboardDescription(config.description);
        setWidgets(config.widgets || []);
        setLayouts(config.layouts || { lg: [], md: [], sm: [], xs: [] });
        setSelectedDashboard(response.data);
        localStorage.setItem('lastSelectedDashboardId', response.data.id);
      } else {
        setError(`Dashboard "${name}" not found.`);
      }
    } catch (error) {
      console.error('Failed to load dashboard by name:', error);
      setError(`Dashboard "${name}" not found or you don't have permission to edit it.`);
    }
  };


  const handleAddWidget = () => {
    setEditingWidget(null);
    setIsModalOpen(true);
  };

  // Removed handleAddWidgetFromSidebar as it's no longer needed

  const handleEditWidget = (widget) => {
    console.log('Edit widget function called:', widget);
    setEditingWidget(widget);
    setIsModalOpen(true);
  };

  const handleSaveWidget = (widgetConfig) => {
    if (editingWidget) {
      // Update existing widget
      setWidgets(prev => prev.map(w => 
        w.id === editingWidget.id ? { ...w, ...widgetConfig } : w
      ));
    } else {
      // Add new widget
      const newWidget = {
        id: Date.now().toString(),
        ...widgetConfig,
        x: 0,
        y: 0,
        w: widgetConfig.type === 'single-metric' ? 2 : widgetConfig.type === 'custom-table' ? 8 : 6,
        h: widgetConfig.type === 'single-metric' ? 2 : widgetConfig.type === 'custom-table' ? 6 : 4
      };
      
      setWidgets(prev => [...prev, newWidget]);
      
      // Add to layout
      setLayouts(prev => ({
        lg: [...prev.lg, {
          i: newWidget.id,
          x: 0,
          y: 0,
          w: newWidget.w,
          h: newWidget.h
        }],
        md: [...prev.md, {
          i: newWidget.id,
          x: 0,
          y: 0,
          w: newWidget.w,
          h: newWidget.h
        }],
        sm: [...prev.sm, {
          i: newWidget.id,
          x: 0,
          y: 0,
          w: newWidget.w,
          h: newWidget.h
        }],
        xs: [...prev.xs, {
          i: newWidget.id,
          x: 0,
          y: 0,
          w: newWidget.w,
          h: newWidget.h
        }]
      }));
    }
    setIsModalOpen(false);
    setEditingWidget(null);
  };

  const handleDeleteWidget = (widgetId) => {
    console.log('Delete widget function called:', widgetId);
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
    setLayouts(prev => ({
      lg: prev.lg.filter(item => item.i !== widgetId),
      md: prev.md.filter(item => item.i !== widgetId),
      sm: prev.sm.filter(item => item.i !== widgetId),
      xs: prev.xs.filter(item => item.i !== widgetId)
    }));
  };

  const handleLayoutChange = (layout, layouts) => {
    setLayouts(layouts);
  };

  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    const theme = newDarkMode ? 'dark' : 'light';
    
    try {
      const response = await API.post('/auth/update-theme', {
        theme: theme
      });

      if (response.data.message) {
        setDarkMode(newDarkMode);
        setSettingsOpen(false);
        localStorage.setItem('darkMode', newDarkMode.toString());
      }
    } catch (error) {
      console.error('Failed to update theme:', error);
      // Still update locally even if API fails
      setDarkMode(newDarkMode);
      setSettingsOpen(false);
      localStorage.setItem('darkMode', newDarkMode.toString());
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    setSettingsOpen(false);
  };

  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen);
    setDropdownOpen(false);
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/settings/profile");
  };

  const handleChangePasswordClick = () => {
    setDropdownOpen(false);
    navigate("/change-password");
  };

  const handleBackToDashboards = () => {
    navigate('/dashboards');
  };

  const handleViewDashboard = () => {
    if (dashboardName) {
      navigate(`/dashboards/${encodeURIComponent(dashboardName)}/dashboard-viewer`);
    }
  };

  const handleSaveDashboard = async () => {
    if (!dashboardName.trim()) {
      alert('Please enter a dashboard name');
      return;
    }

    try {
      const dashboardConfig = {
        name: dashboardName,
        description: dashboardDescription,
        widgets,
        layouts
      };

      const requestData = {
        name: dashboardName,
        description: dashboardDescription,
        dashboardConfig: JSON.stringify(dashboardConfig),
        isDefault: false
      };

      let response;
      if (selectedDashboard && selectedDashboard.id) {
        // Update existing dashboard
        response = await API.put(`/api/dashboard/${selectedDashboard.id}`, requestData);
        alert('Dashboard updated successfully!');
      } else {
        // Create new dashboard
        response = await API.post('/api/dashboard', requestData);
        alert('Dashboard saved successfully!');
      }
      
      // Update selected dashboard and save to localStorage
      setSelectedDashboard(response.data);
      localStorage.setItem('lastSelectedDashboardId', response.data.id);
      
      // Refresh dashboard list
      await fetchDashboards();
    } catch (error) {
      console.error('Failed to save dashboard:', error);
      alert('Failed to save dashboard: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLoadDashboard = async (dashboard) => {
    try {
      const response = await API.get(`/api/dashboard/${dashboard.id}`);
      const config = JSON.parse(response.data.dashboardConfig);
      
      setDashboardName(config.name);
      setDashboardDescription(config.description);
      setWidgets(config.widgets || []);
      setLayouts(config.layouts || { lg: [], md: [], sm: [], xs: [] });
      setSelectedDashboard(dashboard);
      
      // Save to localStorage for next page load
      localStorage.setItem('lastSelectedDashboardId', dashboard.id);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      alert('Failed to load dashboard');
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.user-dropdown')) {
        setDropdownOpen(false);
      }
      if (settingsOpen && !event.target.closest('.settings-dropdown')) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen, settingsOpen]);

  if (loading) {
    return (
      <div className={`dashboard-builder ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px' 
        }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`dashboard-builder ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleBackToDashboards} className="action-button primary">
            Back to Dashboards
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-builder ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Blue Header Stripe */}
      <header className="dashboard-header commray-no-print">
        <div className="header-content">
          {/* Commray Branding - Left Side */}
          <div className="brand-section">
            <h1 className="brand-name">Commray</h1>
          </div>

          {/* Dashboard Title - Center */}
          <div className="center-title">
            <h2 className="dashboard-title-header">
              {dashboardName || 'Dashboard Builder'}
            </h2>
            <span className="dashboard-subtitle">
              {isEditMode ? 'Edit Mode' : 'View Mode'}
            </span>
          </div>

          {/* Right Side Controls */}
          <div className="right-controls">
            <AskCommrayAi />
            {/* Navigation Buttons */}
            <button 
              className="back-button"
              onClick={handleBackToDashboards}
              title="Back to Dashboards"
            >
              ← Back
            </button>

            {dashboardName && (
              <button 
                className="view-button"
                onClick={handleViewDashboard}
                title="View Dashboard"
              >
                👁️ View
              </button>
            )}

            {/* Settings Button */}
            <div className="settings-dropdown">
              <button className="settings-button" onClick={toggleSettings}>
                <span className="settings-icon">⚙️</span>
              </button>

              {settingsOpen && (
                <div className="settings-menu">
                  <div className="settings-header">
                    <h4>Settings</h4>
                  </div>
                  <div className="settings-divider"></div>
                  <div className="settings-item" onClick={toggleDarkMode}>
                    <span className="settings-icon">
                      {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                    </span>
                    <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </div>
                  <UserTimezoneSelect />
                  <div className="settings-divider" />
                  <div
                    className={`settings-item${captureBusy || !widgets.length ? ' settings-item--disabled' : ''}`}
                    onClick={() => {
                      if (captureBusy || !widgets.length) return;
                      setSettingsOpen(false);
                      handlePrintPage();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">🖨</span>
                    <span>Print page</span>
                  </div>
                  <div
                    className={`settings-item${captureBusy || !widgets.length ? ' settings-item--disabled' : ''}`}
                    onClick={() => {
                      if (captureBusy || !widgets.length) return;
                      setSettingsOpen(false);
                      void handleExportDashboardPng();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">📷</span>
                    <span>Export dashboard as PNG</span>
                  </div>
                  <div
                    className={`settings-item${captureBusy || !widgets.length ? ' settings-item--disabled' : ''}`}
                    onClick={() => {
                      if (captureBusy || !widgets.length) return;
                      setSettingsOpen(false);
                      void handleExportWidgetPngs();
                    }}
                    role="menuitem"
                  >
                    <span className="settings-item-icon">📷</span>
                    <span>Export widgets as PNG</span>
                  </div>
                </div>
              )}
            </div>

            {/* User Avatar Dropdown */}
            <div className="user-dropdown">
              <button className="user-avatar" onClick={toggleDropdown}>
                <span className="avatar-circle">
                  {userInfo.username ? userInfo.username.charAt(0).toUpperCase() : 'U'}
                </span>
              </button>

              {dropdownOpen && (
                <div className="user-menu">
                  <div className="user-info">
                    <div className="user-name">{userInfo.username || 'User'}</div>
                    <div className="user-role">{userInfo.role || 'USER'}</div>
                  </div>
                  <div className="user-divider"></div>
                  <div className="user-item" onClick={handleProfileClick}>
                    Profile
                  </div>
                  <div className="user-item" onClick={handleChangePasswordClick}>
                    Change Password
                  </div>
                  <div className="user-divider"></div>
                  <div className="user-item logout" onClick={handleLogout}>
                    Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Left Sidebar - Dashboard Management */}
      <div className="dashboard-sidebar commray-no-print">
        <div className="sidebar-header">
          <h3>Dashboard Management</h3>
        </div>
        <div className="sidebar-content">
          {/* Time Range Section */}
          <div className="sidebar-section time-range-section">
            <div className="sidebar-dropdown-header" onClick={() => setTimeRangeOpen(!timeRangeOpen)}>
              <h4>⏱️ Time Range</h4>
              <span className={`dropdown-arrow ${timeRangeOpen ? 'open' : ''}`}>▼</span>
            </div>
            {timeRangeOpen && (
              <div className="sidebar-dropdown-content">
                <DashboardTimeRangeControls
                  selection={timeSelection}
                  onChange={updateTimeSelection}
                />
                <button
                  type="button"
                  className="sidebar-btn secondary"
                  style={{ marginTop: '10px', width: '100%' }}
                  onClick={clearDashboardTime}
                >
                  Clear All Filters
                </button>
                <button
                  type="button"
                  className="sidebar-btn secondary"
                  style={{ marginTop: '8px', width: '100%' }}
                  onClick={refreshNow}
                >
                  <RefreshCw size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Refresh Data
                </button>
              </div>
            )}
          </div>

          {/* Dashboard Controls Section */}
          <div className="sidebar-section">
            <h4>Dashboard Settings</h4>
            <div className="sidebar-input-group">
              <input
                type="text"
                placeholder="Dashboard Name"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                className="sidebar-input"
              />
              <input
                type="text"
                placeholder="Description"
                value={dashboardDescription}
                onChange={(e) => setDashboardDescription(e.target.value)}
                className="sidebar-input"
              />
            </div>
            <div className="sidebar-button-group">
              <button onClick={toggleEditMode} className="sidebar-btn secondary">
                {isEditMode ? <Eye className="btn-icon" /> : <Settings className="btn-icon" />}
                {isEditMode ? 'View Mode' : 'Edit Mode'}
              </button>
              <button onClick={handleSaveDashboard} className="sidebar-btn success">
                <Save className="btn-icon" />
                Save Dashboard
              </button>
            </div>
          </div>

          {/* Saved Dashboards Section */}
          <div className="sidebar-section">
            <div className="sidebar-dropdown-header" onClick={() => setSavedDashboardsOpen(!savedDashboardsOpen)}>
              <h4>Saved Dashboards</h4>
              <span className={`dropdown-arrow ${savedDashboardsOpen ? 'open' : ''}`}>▼</span>
            </div>
            {savedDashboardsOpen && (
              <div className="sidebar-dropdown-content">
                {savedDashboards.map((savedDashboard) => (
                  <div
                    key={savedDashboard.id}
                    className={`sidebar-dashboard-item ${selectedDashboard?.id === savedDashboard.id ? 'active' : ''}`}
                    onClick={() => handleLoadDashboard(savedDashboard)}
                  >
                    <div className="dashboard-item-name">{savedDashboard.name}</div>
                    <div className="dashboard-item-actions">
                      <button
                        className="sidebar-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboards/${encodeURIComponent(savedDashboard.name)}/dashboard-viewer`);
                        }}
                        title="View Dashboard"
                      >
                        👁️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Widget Controls Section */}
          {isEditMode && (
            <div className="sidebar-section">
              <h4>Widget Controls</h4>
              <button 
                className="sidebar-widget-btn primary"
                onClick={handleAddWidget}
              >
                <Plus size={16} />
                Add Widget
              </button>
            </div>
          )}

          {/* Current Widgets Section */}
          {isEditMode && widgets.length > 0 && (
            <div className="sidebar-section">
              <h4>Current Widgets</h4>
              <div className="widget-list">
                {widgets.map((widget) => (
                  <div key={widget.id} className="widget-list-item">
                    <div className="widget-item-info">
                      <span className="widget-item-title">{widget.title || 'Untitled Widget'}</span>
                      <span className="widget-item-type">{widget.type}</span>
                    </div>
                    <div className="widget-item-actions">
                      <button
                        className="widget-action-btn edit"
                        onClick={() => handleEditWidget(widget)}
                        title="Edit Widget"
                      >
                        <Settings size={14} />
                      </button>
                      <button
                        className="widget-action-btn delete"
                        onClick={() => handleDeleteWidget(widget.id)}
                        title="Delete Widget"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Main Dashboard Content */}
      <main className="dashboard-content">
        {/* Dashboard Grid */}
        <div ref={dashboardGridRef} className="dashboard-grid">
        <DashboardChartSyncProvider dashboardKey={selectedDashboard?.id || dashboardName || urlDashboardName || 'builder'}>
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          onLayoutChange={handleLayoutChange}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={60}
          isDraggable={isEditMode}
          isResizable={isEditMode}
          resizeHandles={['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne']}
          margin={[8, 8]}
          containerPadding={[4, 4]}
        >
          {widgets && widgets.length > 0 ? widgets.map((widget) => {
            if (!widget || !widget.id) {
              return null;
            }
            return (
              <div key={widget.id} className={`widget-container commray-widget-capture theme-${widget.theme || 'blue'}`}>
                <div className="widget-header">
                  <div className="widget-title-right">
                    <h3>{widget.title || 'Untitled Widget'}</h3>
                  </div>
                </div>
                <div className="widget-content">
                  <WidgetRenderer widget={widget} startTime={startTime} endTime={endTime} refreshTrigger={refreshTrigger} />
                </div>
              </div>
            );
          }) : null}
        </ResponsiveGridLayout>
        </DashboardChartSyncProvider>
        </div>
      </main>

      {/* Widget Modal */}
      <WidgetModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingWidget(null);
        }}
        onSave={handleSaveWidget}
        widget={editingWidget}
        darkMode={darkMode}
      />
    </div>
  );
};

export default DashboardBuilder;
